import LoadingBox from 'components/Common/Loading/LoadingBox'
import IconButton from 'LuxKit/Button/IconButton'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import { rem } from 'polished'
import { useCallback } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import {
  SearchMapCardRoot,
  SearchMapCardImageContainer,
  searchMapCardImageParams,
} from './SearchMapCardStyles'

const ActionButtons = styled.div`
  position: absolute;
  top: ${rem(10)};
  inset-inline-end: ${rem(10)};
  display: flex;
  gap: ${rem(8)};
  z-index: 1;
`

const BookmarkWrapper = styled.div`
  && button {
    --icon-button-icon-size: ${rem(16)};
  }
`

const ImageLoadingBox = styled(LoadingBox)`
  height: 100%;
  min-height: ${rem(140)};
`

const BodyContainer = styled(Group)`
  padding: ${rem(12)};
`

interface Props {
  images?: Array<App.Image>
  onClose?: () => void
  bookmarkButton?: React.ReactNode
  imageBottomLeft?: React.ReactNode
  children?: React.ReactNode
  isLoading?: boolean
}

function SearchMapCardLayout(props: Props) {
  const {
    images = [],
    onClose,
    bookmarkButton,
    imageBottomLeft,
    children,
    isLoading = false,
  } = props

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const handleClose = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    onClose?.()
  }, [onClose])

  return (
    <SearchMapCardRoot data-testid={isLoading ? 'search-map-card-loading' : undefined}>
      <SearchMapCardImageContainer>
        {isLoading ? (
          <ImageLoadingBox />
        ) : (
          <ImageCarousel
            arrowBehaviour={isTabletScreenSizeAndUp ? 'interaction-visible' : 'hidden'}
            images={images}
            imageParams={searchMapCardImageParams}
            eagerLoadFirstImage
            bottomLeft={imageBottomLeft}
          />
        )}
        <ActionButtons>
          {!isLoading && bookmarkButton && (
            <CSSBreakpoint max="mobile">
              <BookmarkWrapper>
                {bookmarkButton}
              </BookmarkWrapper>
            </CSSBreakpoint>
          )}
          {onClose && (
            <IconButton
              size="small"
              shape="circle"
              kind="primary"
              onClick={handleClose}
              variant="ghost"
            >
              <LineTimesIcon />
            </IconButton>
          )}
        </ActionButtons>
      </SearchMapCardImageContainer>
      {isLoading ? (
        <BodyContainer direction="vertical" gap={8}>
          <Group direction="vertical" gap={4}>
            <TextLoadingBox typography="caption-large" width="55%" />
            <TextLoadingBox typography="body-medium" width="100%" lines={2} />
          </Group>
          <LoadingBox height={40} width={120} borderRadius="S" />
        </BodyContainer>
      ) : (
        children
      )}
    </SearchMapCardRoot>
  )
}

export default SearchMapCardLayout
